import { useCallback, useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { uploadCashPhoto } from '@/lib/parkingPhotoUpload'
import { paymentKeys } from '@/hooks/usePayments'
import type { PaginationMeta } from '@/types'
import type {
  ParkingSession,
  ParkingSessionCounts,
  ParkingSessionDetail,
  ParkingSessionState,
} from '@/types/parking'

// EP-161: ที่จอดรถวันนี้. One property's visitor cars and their payment state.

export const PARKING_SESSION_STATES: readonly ParkingSessionState[] = [
  'parked',
  'overstay',
  'awaiting_payment',
  'slip_to_verify',
  'rejected',
  'paid',
]

export function isParkingSessionState(value: string): value is ParkingSessionState {
  return (PARKING_SESSION_STATES as readonly string[]).includes(value)
}

// The photo and slip keys are bare storage keys, rendered through
// /api/images/<key> the way PaymentDetailPage renders a parking slip.
const apiBase = import.meta.env.VITE_API_URL || ''
export function parkingImageUrl(key: string): string {
  return key.startsWith('http') ? key : `${apiBase}/api/images/${key}`
}

export const parkingSessionKeys = {
  all: ['external_parking_sessions'] as const,
  lists: () => [...parkingSessionKeys.all, 'list'] as const,
  list: (filters: Record<string, unknown>) => [...parkingSessionKeys.lists(), filters] as const,
  details: () => [...parkingSessionKeys.all, 'detail'] as const,
  detail: (id: number) => [...parkingSessionKeys.details(), id] as const,
}

// The list refetches on this interval while the page is open. TanStack Query
// pauses interval refetches while the tab is hidden (refetchIntervalInBackground
// defaults to false), so a backgrounded tab does not keep polling.
export const PARKING_SESSIONS_REFETCH_MS = 30_000

export interface ParkingSessionFilters {
  propertyId: number
  /** YYYY-MM-DD; absent means today in the account timezone (the server's default). */
  date?: string | null
  allDays?: boolean
  states?: ParkingSessionState[]
  q?: string
  page?: number
}

export interface ParkingSessionsResponse {
  data: ParkingSession[]
  meta: PaginationMeta & { counts: ParkingSessionCounts }
}

export function useParkingSessions(filters: ParkingSessionFilters) {
  const { propertyId, date, allDays, states = [], q, page = 1 } = filters
  const keyFilters = { propertyId, date: date ?? null, allDays: !!allDays, states, q: q ?? '', page }
  return useQuery({
    queryKey: parkingSessionKeys.list(keyFilters),
    enabled: propertyId > 0,
    refetchInterval: PARKING_SESSIONS_REFETCH_MS,
    queryFn: async () => {
      const params: Record<string, string | number> = { property_id: propertyId, page }
      if (allDays) params.all_days = 1
      else if (date) params.date = date
      if (states.length > 0) params.state = states.join(',')
      if (q && q.trim()) params.q = q.trim()
      const { data } = await api.get<ParkingSessionsResponse>('/api/external_parking_sessions', { params })
      return data
    },
  })
}

export function useParkingSession(id: number | null) {
  return useQuery({
    queryKey: parkingSessionKeys.detail(id ?? 0),
    enabled: id != null && id > 0,
    refetchInterval: PARKING_SESSIONS_REFETCH_MS,
    queryFn: async () => {
      const { data } = await api.get<{ data: ParkingSessionDetail }>(`/api/external_parking_sessions/${id}`)
      return data.data
    },
  })
}

/** After a Verify or Reject on a session's payment: the row's state changed. */
export function useInvalidateParkingSessions() {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: parkingSessionKeys.all })
}

export function useMarkParkingSessionFraud() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      api.post(`/api/external_parking_sessions/${id}/fraud`, { reason }),
    // The modal shows the refusal inline, mapped from its code.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: parkingSessionKeys.all })
      // The mark rejects the kiosk payment.
      queryClient.invalidateQueries({ queryKey: paymentKeys.all })
    },
  })
}

/**
 * EP-161 §2.3, the gate (T161-R03b): charges a parked car exactly as the
 * kiosk's "Pay now" does and answers the detail row, whose payment carries
 * the PromptPay payload for the QR. Refusals (already_exited,
 * overstay_contact_admin, no_fee_configured) are handled by the caller.
 */
export function useCheckOutParkingSession() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const { data } = await api.post<{ data: ParkingSessionDetail }>(`/api/external_parking_sessions/${id}/check_out`)
      return data.data
    },
    meta: { suppressErrorToast: true },
    onSuccess: (row) => {
      queryClient.setQueryData(parkingSessionKeys.detail(row.id), row)
      queryClient.invalidateQueries({ queryKey: parkingSessionKeys.lists() })
    },
    onError: () => {
      // already_exited: the row was stale, so read the list again.
      queryClient.invalidateQueries({ queryKey: parkingSessionKeys.lists() })
    },
  })
}

export interface CashPhoto {
  key: string
  /** An object URL for the thumbnail; null where the browser cannot make one. */
  previewUrl: string | null
}

/** What the photo step reports: a refusal code, or `upload_failed`. */
export type CashPhotoError = string

function previewOf(file: File): string | null {
  return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null
}

function revoke(url: string | null) {
  if (url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
}

/**
 * The cash photo of one session's settlement: uploaded at once when taken
 * (the presign lasts 180 seconds), shown as a thumbnail, and dropped by
 * `reset` when the server refuses it (stale or invalid) so it is taken again.
 */
export function useCashPhoto(sessionId: number) {
  const [photo, setPhoto] = useState<CashPhoto | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<CashPhotoError | null>(null)
  const previewRef = useRef<string | null>(null)

  useEffect(() => () => revoke(previewRef.current), [])

  const reset = useCallback((code: CashPhotoError | null = null) => {
    revoke(previewRef.current)
    previewRef.current = null
    setPhoto(null)
    setError(code)
  }, [])

  const select = useCallback(
    async (file: File) => {
      reset()
      setUploading(true)
      try {
        const key = await uploadCashPhoto(sessionId, file)
        const previewUrl = previewOf(file)
        previewRef.current = previewUrl
        setPhoto({ key, previewUrl })
      } catch (e) {
        const code = (e as { response?: { data?: { error?: { code?: string } } } }).response?.data?.error?.code
        setError(code ?? 'upload_failed')
      } finally {
        setUploading(false)
      }
    },
    [sessionId, reset],
  )

  return { photo, uploading, error, select, reset }
}
